import { useAction, useAtom } from '@reatom/npm-react'
import styled from 'styled-components'

import { useTranslation } from '@root/shared/localization'
import { ChargeIcon, Input } from '@root/shared/ui-kit'

import {
  basicQuickBuyAmountRawAtom,
  resetBasicQuickBuyAmountAction,
  setBasicQuickBuyAmountRawAction,
} from '../atoms/reatom-basic-quick-buy'

import type { ClassNameProps } from '@root/shared/react'

type Props = {
  withLabel?: boolean
  size?: 'h28' | 'h32' | 'h36'
} & ClassNameProps

/**
 * How much a quick buy spends, in dollars.
 *
 * One number and nothing beside it. The advanced version carries a chain
 * icon, a converted dollar figure and a gear for slippage and fees; in Basic
 * the amount is already dollars and the server decides the rest.
 */
export const BasicQuickBuyInput: React.FC<Props> = ({
  withLabel = true,
  size = 'h36',
  className,
}) => {
  const { t } = useTranslation()
  const [raw] = useAtom(basicQuickBuyAmountRawAtom)
  const setRaw = useAction(setBasicQuickBuyAmountRawAction)
  const reset = useAction(resetBasicQuickBuyAmountAction)

  return (
    <Root
      numeric
      autosize
      decimalScale={2}
      size={size}
      className={className}
      value={raw}
      onChange={(event) => setRaw(event.target.value)}
      onBlur={() => reset()}
      startContent={
        <Start>
          <Charge size={16} />
          {withLabel ? <span>{t('trading.buy')}</span> : undefined}
        </Start>
      }
      endContent={<Dollar>$</Dollar>}
    />
  )
}

const Root = styled(Input)`
  --input-padding-inline: 12px;
  --input-start-content-spacing: 8px;

  input {
    padding-right: 2px;
  }
`

const Start = styled.span`
  display: grid;
  grid-auto-flow: column;
  align-items: center;
  gap: 4px;

  color: var(--text-11);
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
`

const Charge = styled(ChargeIcon)`
  color: var(--primary-9);
`

const Dollar = styled.span`
  color: var(--text-11);
  font-size: 13px;
  font-weight: 600;
`
